import { Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import * as qs from 'qs';
import React, { useEffect, useState } from 'react';
import AceEditor from "react-ace";
import { RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import LinkRouter from '../../common/LinkRouter';
import LoadingIndicator from '../../common/LoadingIndicator';
import FaroParametersTable from '../../faro/parameters/FaroParametersTable';
import ExecutionDeploymentActionStateParser from './ExecutionDeploymentActionStateParser';
import ExecutionActionStatusChip from './ExecutionActionStatusChip';
import { ActionState } from './ExecutionConstants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    header: {
      marginBottom: 24,
      display: 'flex', 
      alignItems: 'center'
    },
    tabs: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: darken(theme.palette.background.default, 0.03),
      marginBottom: 24
    },
    alert: {
      margin: '16px 0'
    },
    headerText: { 
      marginLeft: 16 
    }
  })
);

enum LifecycleState {
  Deploy,
  Teardown,
  Complete
}

const windsorApi = new WindsorApi();

function getLogTimestamp() {
  return moment().utc().format('YYYY/MM/DD HH:mm:ss');
}

function ExecutionDeploymentActionDetailsPage(props: RouteComponentProps<any> & WithSnackbarProps) {
  const { match, history, location } = props;
  const familyId = match.params.familyId;
  const executionId = match.params.executionId;
  const actionName = match.params.actionName;
  const { tab = 'logs' } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const classes = useStyles({});

  const [execution, setExecution] = useState(null);
  const [errors, setErrors] = useState([]);
  const [parameters, setParameters] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);
  const [deploymentLogs, setDeploymentLogs] = useState('');
  const [lifecycleState, setLifecycleState] = useState(null);

  useEffect(() => {
    if (execution) {
      setActionStatus(new ExecutionDeploymentActionStateParser(execution).getActionState(actionName));
      const action = execution.DeploymentList.find(item => item.ActionName === actionName);
      if (action && action.Errors) {
        setErrors(action.Errors);
      }
    } else {
      windsorApi.getExecution(familyId, executionId).then(execution => {
        setExecution(execution);
        setLifecycleState(execution.ExecutionPlan === 'TEARDOWN_ALL' ? LifecycleState.Teardown : LifecycleState.Deploy);
        const actionFromWorkflowCache = execution.WorkflowCache.DeploymentRecipe.find(item => item.ActionName === actionName);
        if (actionFromWorkflowCache && actionFromWorkflowCache.Parameters) {
          const paramKeys = Object.keys(actionFromWorkflowCache.Parameters);
          setParameters(paramKeys.length ? paramKeys.map(key => ({ name: key, value: actionFromWorkflowCache.Parameters[key] })) : null);
        }
      });
    }
  }, [execution, familyId, executionId, actionName]);

  useEffect(() => {
    const scheduleExecutionQuery = () => {
      const timeoutHandle = setTimeout(() => windsorApi.getExecution(familyId, executionId).then(execution => {
        setExecution(execution);
      }), 10000);
      return () => clearTimeout(timeoutHandle);
    };

    const loadDeployLogs = () => {
      return windsorApi.getExecutionActionDeployLogs(familyId, executionId, actionName).then(logs => {
        setDeploymentLogs(prevLogs => prevLogs + logs);
      });
    };

    const loadTeardownLogs = () => {
      return windsorApi.getExecutionActionTeardownLogs(familyId, executionId, actionName).then(logs => {
        setDeploymentLogs(prevLogs => prevLogs + logs);
      });
    };

    const updateDeploymentState = (action, execution) => {
      return windsorApi.getFaroDeployment(action.DeploymentName, execution.AccountCache.AccountID, execution.AccountCache.Region, action.Global).then(response => {
        if (response.State !== 'COMPLETE') {
          setDeploymentLogs(prevDeploymentLogs => prevDeploymentLogs + `${getLogTimestamp()} [INFO] Deployment '${action.DeploymentName}' has status of '${response.State}'\n`);
        }
      }).catch(error => {
        if (error.status !== 404) {
          console.error('Failed to query for deployment');
        }
      });
    }

    if (execution !== null && lifecycleState !== null && actionStatus !== ActionState.NotStarted) {
      const action = execution.DeploymentList.find(item => item.ActionName === actionName);
      if (!action && actionStatus !== ActionState.Unknown) {
        // Deployment is still being initialized
        const deploymentDetails = execution.WorkflowCache.DeploymentRecipe.find(item => item.ActionName === actionName);
        setDeploymentLogs(prevLog => prevLog + `${getLogTimestamp()} [INFO] Deployment '${deploymentDetails ? deploymentDetails.DeploymentName : 'unknown'}' has status of PENDING\n`);
        return scheduleExecutionQuery();
      } else if (!action) {
        // Action state is unkown.
        setLifecycleState(LifecycleState.Complete);
      } else {
        if (lifecycleState === LifecycleState.Deploy) {
          // Check if deploy is complete or failed
          if (action.DeploymentState.includes('FAILED') || (action.Errors && action.Errors.length)) {
            loadDeployLogs().then(() => setLifecycleState(LifecycleState.Complete));
          } else if (action.DeploymentState === 'COMPLETE') {
            loadDeployLogs().then(() => {
              if (execution.ExecutionPlan === 'DEPLOY_WITH_TEARDOWN' || execution.ExecutionPlan === 'DEPLOY_TEARDOWN') {
                setLifecycleState(LifecycleState.Teardown);
              } else {
                setLifecycleState(LifecycleState.Complete);
              }
            });
          } else {
            // Get current state of deployment
            updateDeploymentState(action, execution);
            return scheduleExecutionQuery();
          }
        } else if (lifecycleState === LifecycleState.Teardown) {
          // Check if teardown is complete or failed
          if (action.TeardownState.includes('FAILED') || (action.Errors && action.Errors.length)) {
            loadTeardownLogs().then(() => setLifecycleState(LifecycleState.Complete));
          } else if (action.TeardownState === 'TORN_DOWN') {
            loadTeardownLogs().then(() => setLifecycleState(LifecycleState.Complete));
          } else {
            // Get current state of deployment
            updateDeploymentState(action, execution);
            return scheduleExecutionQuery();
          }
        }
      }
    }
  }, [lifecycleState, execution, actionStatus, actionName, executionId, familyId]);

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    history.push({ search: `?tab=${newValue}` });
  };

  return execution && actionStatus && (actionStatus === ActionState.NotStarted || actionStatus === ActionState.Unknown || !!deploymentLogs) ? (
    <div className={classes.root}>
      <Breadcrumbs aria-label="breadcrumb">
        <LinkRouter color="textSecondary" to={`/families`}>
          Families
          </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}`}>
          {execution.FamilyCache.Name}
        </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows`}>
          Workflows
        </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows/${execution.WorkflowCache.GUID}`}>
          {execution.WorkflowName}
        </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows/${execution.WorkflowCache.GUID}?tab=executions`}>
          Executions
        </LinkRouter>
        <LinkRouter color="textSecondary" to={`/families/${familyId}/executions/${execution.GUID}`}>
          {execution.GUID}
        </LinkRouter>
        <Typography color="textPrimary">{actionName}</Typography>
      </Breadcrumbs>
      <Divider className={classes.divider} />
      <div className={classes.header}>
        <ExecutionActionStatusChip state={actionStatus} />
        <Typography variant="h6" className={classes.headerText}>
          Deployment details for action: {actionName}
        </Typography>
      </div>
      {errors && errors.length > 0 && <Alert severity="error" className={classes.alert}>
        {errors.map((error, index) => <Typography key={index}>{error}</Typography>)}
      </Alert>}
      <Tabs
        className={classes.tabs}
        value={tab}
        onChange={onTabChange}
        aria-label="Action Details Tabs"
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Logs" value="logs" />
        <Tab label="Parameters" value="parameters" />
      </Tabs>
      {tab === 'logs' && <AceEditor
        readOnly
        wrapEnabled
        mode="powershell"
        theme={'monokai'}
        width="100%"
        minLines={25}
        maxLines={Infinity}
        value={deploymentLogs}
        setOptions={{ useWorker: false }}
        showPrintMargin={false}
        editorProps={{ $blockScrolling: true }}
      />}
      {tab === 'parameters' && <div>
        {parameters && <FaroParametersTable hideKmsKeyArn items={parameters} />}
        {!parameters && <Typography color="textSecondary">This action has no parameters</Typography>}
      </div>}
    </div >
  ) : <LoadingIndicator loading={true} />;
};

export default withRouter(withSnackbar(ExecutionDeploymentActionDetailsPage));
